// Yeniliklər paneli — sağdan sürüşərək açılır.
//
// NİYƏ MODAL DEYİL, YAN PANEL: POS ekranı iş alətidir. Ortada açılan pəncərə
// masa şəbəkəsini və açıq hesabı tamamilə örtür — işçi nə etdiyini itirir.
// Yan panel isə ekranın bir hissəsini saxlayır, istənilən an bağlanır və
// heç nəyi dayandırmır.
//
// Bütün buraxılışların qeydləri göstərilir, təkcə sonuncunun yox: restoran
// bir müddət bağlı qalıb bir neçə versiya geridə düşə bilər və aradakı
// dəyişiklikləri də görməlidir.

import React from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, Download, RefreshCw, X } from 'lucide-react';

import type { ReleaseNote, UpdatePhase } from '../hook/useAppUpdate';

export type Translate = (key: string, vars?: Record<string, string | number>) => string;

/** `0.10.0` → 10000 — MƏTN müqayisəsi `0.9.0`-ı böyük sayardı. */
export function versionRank(v: string): number {
  const p = String(v || '0')
    .split('-')[0]
    .split('.')
    .map((n) => parseInt(n, 10) || 0);
  return (p[0] || 0) * 1e6 + (p[1] || 0) * 1e3 + (p[2] || 0);
}

/** `1787666315625` → `14:38` */
function hhmm(ms?: number): string {
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

interface Props {
  open: boolean;
  onClose: () => void;
  onInstall: () => void;
  onCheck: () => void;
  currentVersion: string;
  phase: UpdatePhase;
  newVersion?: string;
  lastCheckedAt?: number;
  notes: ReleaseNote[];
  lang: string;
  t: Translate;
}

const UpdateDrawer: React.FC<Props> = ({
  open,
  onClose,
  onInstall,
  onCheck,
  currentVersion,
  phase,
  newVersion,
  lastCheckedAt,
  notes,
  lang,
  t,
}) => {
  const currentRank = versionRank(currentVersion);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Arxa fon: yüngül qaralma — panelin harada bitdiyi bilinsin,
              amma arxadakı ekran hələ də oxunsun. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-[390] bg-black/25"
          />

          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 380, damping: 38 }}
            className="fixed right-0 top-0 z-[400] flex h-full w-full max-w-md flex-col bg-white shadow-2xl dark:bg-[#1b1c22]"
          >
            <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 dark:border-white/10">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t('update.title')}
                </h2>
                <div className="mt-0.5 font-mono text-xs text-gray-400">v{currentVersion}</div>
                <StatusLine
                  phase={phase}
                  newVersion={newVersion}
                  lastCheckedAt={lastCheckedAt}
                  t={t}
                />
              </div>
              <button
                onClick={onClose}
                className="shrink-0 rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10"
                aria-label={t('update.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {notes.length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-400">{t('update.noNotes')}</p>
              ) : (
                <ol className="space-y-5">
                  {notes.map((n) => {
                    const text = n.notes?.[lang] || n.notes?.az || '';
                    const isNewer = versionRank(n.version) > currentRank;
                    const isCurrent = n.version === currentVersion;
                    return (
                      <li
                        key={n.version}
                        className={
                          isNewer
                            ? 'rounded-xl border border-orange-500/30 bg-orange-500/5 p-3'
                            : ''
                        }
                      >
                        <div className="mb-1.5 flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-bold text-gray-900 dark:text-white">
                            v{n.version}
                          </span>
                          {isNewer && (
                            <Tag className="bg-orange-500/15 text-orange-600 dark:text-orange-400">
                              {t('update.newLabel')}
                            </Tag>
                          )}
                          {isCurrent && (
                            <Tag className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                              {t('update.currentLabel')}
                            </Tag>
                          )}
                          {n.mandatory && (
                            <Tag className="bg-red-500/15 text-red-600 dark:text-red-400">
                              {t('update.mandatoryLabel')}
                            </Tag>
                          )}
                          {n.date && <span className="text-xs text-gray-400">{n.date}</span>}
                        </div>
                        <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                          {text || '—'}
                        </p>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            <footer className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-3 dark:border-white/10">
              <button
                onClick={onCheck}
                disabled={phase === 'checking'}
                className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-white/10"
              >
                <RefreshCw className={`h-4 w-4 ${phase === 'checking' ? 'animate-spin' : ''}`} />
                {t('update.checkNow')}
              </button>

              {phase === 'ready' && (
                <button
                  onClick={onInstall}
                  className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-bold text-white transition hover:brightness-110"
                >
                  {t('update.restartNow')}
                </button>
              )}
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

const Tag: React.FC<{ className: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <span
    className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${className}`}
  >
    {children}
  </span>
);

/**
 * Bir sətirlik vəziyyət — "güncəldirmi?" sualının cavabı.
 *
 * Vaxt damğası olmadan "güncəldir" demək mənasızdır: bu, bir saat əvvəlki
 * yoxlamaya, yoxsa heç vaxt olmayan yoxlamaya aid olduğu bilinməzdi. Ona görə
 * yoxlama baş verməyibsə bunu açıq deyirik.
 */
const StatusLine: React.FC<{
  phase: UpdatePhase;
  newVersion?: string;
  lastCheckedAt?: number;
  t: Translate;
}> = ({ phase, newVersion, lastCheckedAt, t }) => {
  if (phase === 'downloading') {
    return (
      <Line
        className="text-[var(--primary)]"
        icon={<Download className="h-3.5 w-3.5" />}
        text={t('update.downloading', { version: newVersion || '' })}
      />
    );
  }
  if (phase === 'ready') {
    return (
      <Line
        className="text-[var(--primary)]"
        icon={<RefreshCw className="h-3.5 w-3.5" />}
        text={t('update.ready', { version: newVersion || '' })}
      />
    );
  }
  if (phase === 'checking') {
    return <Line className="text-gray-400" text={t('update.checking')} />;
  }
  if (lastCheckedAt) {
    return (
      <Line
        className="text-emerald-600 dark:text-emerald-400"
        icon={<Check className="h-3.5 w-3.5" />}
        text={t('update.upToDate', { time: hhmm(lastCheckedAt) })}
      />
    );
  }
  return <Line className="text-gray-400" text={t('update.notCheckedYet')} />;
};

const Line: React.FC<{ className: string; text: string; icon?: React.ReactNode }> = ({
  className,
  text,
  icon,
}) => (
  <div className={`mt-1 flex items-center gap-1.5 text-xs font-medium ${className}`}>
    {icon}
    {text}
  </div>
);

export default UpdateDrawer;
